import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Typography } from "antd";
import { Checkbox, Input, TableColumnType, TableColumnsType } from "antd-v5";
import { CheckboxProps } from "antd-v5/lib";
import {
  ColumnOrderChangeCallback,
  ColumnVisibilityChangeCallback,
  ColumnVisibilityConfig,
} from "hooks/useTableCustomizationHelper";
import _ from "lodash";
import React, { useCallback, useRef, useState } from "react";
import { ExportField } from "types/api/exportField";

import ACPopover from "ac-components/ac-popover/ACPopover";

import DraggableVisibilityDropdownItem from "./DraggableVisibilityDropdownItem";

type Props<Record> = {
  children: JSX.Element;
  columnVisibilityConfig: ColumnVisibilityConfig;
  onChange: ColumnVisibilityChangeCallback;
  activeColumns: ExportField[];
  onColumnOrderChange: ColumnOrderChangeCallback;
  columns: TableColumnsType<Record>;
  onResetColumnOptions(): void;
  alwaysVisibleKeys?: React.Key[];
  fixedColumns?: number;
};

export default function ACColumnVisibilityDropdown<Record>({
  children,
  columnVisibilityConfig,
  onChange,
  onColumnOrderChange,
  columns,
  activeColumns,
  onResetColumnOptions,
  alwaysVisibleKeys = [],
  fixedColumns = 1, //fixedColumns is set to 1 by default to prevent the Finding ID column reorder
}: Props<Record>) {
  const inputRef = useRef<HTMLInputElement | any>(null);
  const [searchText, setSearchText] = useState("");
  const onCheckUnCheck: CheckboxProps["onChange"] = useCallback(
    (e) => {
      const key = e.target.id!,
        checked = e.target.checked;
      onChange(key, checked);
    },
    [onChange],
  );
  const _searchText = searchText.toLowerCase();
  const matchedColumns = columns.filter((c) =>
    (c.title as string).toLowerCase().includes(_searchText),
  );
  const sensors = useSensors(useSensor(PointerSensor));
  const columnMap: { [key: React.Key]: TableColumnType<Record> } = {};
  matchedColumns.forEach((c) => (columnMap[c.key!] = c));
  // State to keep track of the item currently being dragged.
  // Its ID is stored here, or it's null if nothing is being dragged.
  const [activeId, setActiveId] = useState<React.Key | null>(null);

  const visibleKeys = _.compact(
    activeColumns
      .map((entry) => entry.field)
      .filter(
        (key) => !alwaysVisibleKeys.includes(key!) && columnMap[key as string],
      ),
  );
  const hiddenKeys = Object.keys(
    _.pickBy(columnVisibilityConfig, (v) => v === false),
  ).filter((key) => columnMap[key]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = visibleKeys.indexOf(active.id as string);
        const newIndex = visibleKeys.indexOf(over.id as string);
        onColumnOrderChange(oldIndex + fixedColumns, newIndex + fixedColumns);
      }
      // When the drag ends, clear the active item's ID to hide the overlay.
      setActiveId(null);
    },
    [fixedColumns, onColumnOrderChange, visibleKeys],
  );

  const dragDropMenu = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      // When a drag starts, we capture the active item's ID in our state.
      // This tells the DragOverlay what to render.
      onDragStart={({ active }) => setActiveId(active.id)}
    >
      <div style={{ maxHeight: 275, maxWidth: 260, overflowY: "auto" }}>
        <SortableContext
          items={visibleKeys}
          strategy={verticalListSortingStrategy}
        >
          {visibleKeys.map((key) => (
            <DraggableVisibilityDropdownItem
              key={key}
              id={key}
              disabled={!!searchText}
              className="ac-dropdown-menu-item ac-column-visibility-dropdown"
              checked={true}
              title={columnMap[key]?.title ?? key}
              onCheckUnCheck={onCheckUnCheck}
            />
          ))}
        </SortableContext>
        {/*
          DragOverlay renders the dragged item in a portal, lifting it out of the
          scrollable container. This prevents the item from disappearing when the item is dragged outside the container
        */}
        <DragOverlay>
          {activeId ? (
            // We render a clone of our item component to act as the visual "ghost".
            // This version is not part of the sortable list; it's just a visual.
            <DraggableVisibilityDropdownItem
              id={activeId}
              disabled={!!searchText}
              className="ac-dropdown-menu-item ac-column-visibility-dropdown is-dragging"
              checked={true}
              title={columnMap[activeId]?.title ?? activeId}
              onCheckUnCheck={onCheckUnCheck}
            />
          ) : null}
        </DragOverlay>
        <div
          className="m-l-xs"
          style={{ padding: "12px 0 4px 0", fontSize: 13, fontWeight: 600 }}
        >
          Not Selected
        </div>

        {hiddenKeys.map((key) => (
          <div
            className="ac-dropdown-menu-item ac-column-visibility-dropdown"
            key={key}
          >
            <Checkbox
              className="w-100"
              id={key as string}
              onChange={onCheckUnCheck}
              checked={false}
            >
              {columnMap[key]?.title ?? key}
            </Checkbox>
          </div>
        ))}
      </div>
    </DndContext>
  );

  return (
    <ACPopover
      onPopupVisibleChange={(v) => {
        setSearchText("");
        if (v) {
          setTimeout(() => inputRef.current.focus(), 0);
        }
      }}
      header="Reorder Columns"
      className="ac-dropdown-menu"
      style={{ padding: "unset" }}
      content={
        <>
          <Input
            ref={inputRef}
            style={{ margin: "8px 0" }}
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            autoFocus
          />
          <div style={{ marginLeft: 15, textDecoration: "underline" }}>
            <Typography.Link onClick={onResetColumnOptions}>
              Reset to Default
            </Typography.Link>
          </div>
          {dragDropMenu}
        </>
      }
      placement="bottomRight"
      trigger={"click"}
    >
      {children}
    </ACPopover>
  );
}
